import {FC, Fragment, useCallback, useEffect} from 'react';
import {useXarrow} from 'react-xarrows';
import classNames from 'classnames';
import CompletedTask from '../completed_task/CompletedTask';
import CurrentTask from '../current_task/CurrentTask';
import {useTaskView} from '../context/task_view.context';
import {ProcessTask} from '../../types';
import {ProcessCode} from '../../../processes/types';
import {TaskCode} from '../../../../constants/tasks';

type TaskRendererProps = {
  enableTransform: () => void,
  disableTransform: () => void,
  zoomToElement: (id: string, scale: number) => void
}

const TaskRenderer: FC<TaskRendererProps> = ({
  disableTransform, enableTransform, zoomToElement
}) => {
  const {
    formattedTasks, taskAlertData, currentTasks,
    addCompletedTaskToArray, successClaimCallback
  } = useTaskView();

  const updateXarrow = useXarrow();

  const getCurrentTasksById = useCallback((taskId) => {
    return currentTasks?.find(({id}) => id === taskId);
  }, [currentTasks]);

  useEffect(() => {
    enableTransform();
    updateXarrow();
  }, [formattedTasks]);

  if (!formattedTasks?.length) {
    return null;
  }

  return formattedTasks.map((column, i) => {
    const hasParallelTasks = column.length > 1;
    const isLastColumn = formattedTasks.length - 1 === i;

    const runningTasks = column.filter((task) =>
      task.status.code === ProcessCode.RUNNING
    );

    return (
      <Fragment key={column[0].id}>
        {hasParallelTasks && <div className="square" id={`arrow_${i}`} />}

        <div className="column">
          {column.map((task: ProcessTask, columnIndex: number) => {
            const isRunningTask = task.status.code === ProcessCode.RUNNING;
            const currentTask = isRunningTask ?
              getCurrentTasksById(task.id) :
              null;

            return (
              <div id={`cArrow_${columnIndex}${i}`} key={task.id}>
                {task.status.code === ProcessCode.RUNNING && !!currentTask ? (
                  <div
                    id={`${task.id}`}
                    className={classNames(
                        'current-task-wrapper', {
                          'single': !hasParallelTasks,
                          'parallel': hasParallelTasks,
                          'employee-survey-task-wrapper': currentTask?.type?.code == TaskCode.COMPLETE_SURVEY,
                          'without-checkboxes': !currentTask?.checklistItems?.length
                        })
                    }
                    onMouseEnter={disableTransform}
                    onTouchStart={disableTransform}
                    onMouseLeave={enableTransform}
                    onTouchEnd={enableTransform}
                  >
                    <CurrentTask
                      runningTasks={runningTasks}
                      task={currentTask}
                      successClaimCallback={successClaimCallback}
                      zoomToElement={zoomToElement}
                      taskAlertData={taskAlertData}
                      addCompletedTaskToArray={addCompletedTaskToArray}
                      notRunningStatus={false}
                    />
                  </div>
                ) : (
                  <CompletedTask
                    task={task}
                    taskAlertData={taskAlertData}
                  />
                )}
              </div>
            );
          })}
        </div>

        {hasParallelTasks && !isLastColumn && <div className="square" id={`aArrow_${i}`} />}
      </Fragment>
    );
  });
};

export default TaskRenderer;
